import {
  DASHBOARD_CHART_CONFIG,
  MetricConfig,
  ChartConfig,
  CHART_COMPONENT_MAP,
} from "../data/chartConfiguartion";

type ComponentResult = {
  module: string;
  key: string;
  config: MetricConfig | ChartConfig;
  type: "metric" | "chart";
};

export class ChartConfigHelper {
  static getMetricConfig(
    module: string | any,
    metricKey: string,
  ): MetricConfig | null {
    return DASHBOARD_CHART_CONFIG[module]?.metrics[metricKey] || null;
  }

  static getChartConfig(
    module: string | any,
    chartKey: string,
  ): ChartConfig | null {
    return DASHBOARD_CHART_CONFIG[module]?.charts[chartKey] || null;
  }

  static getModuleMetrics(module: string): Record<string, MetricConfig> {
    return DASHBOARD_CHART_CONFIG[module]?.metrics || {};
  }

  static getModuleCharts(module: string): Record<string, ChartConfig> {
    return DASHBOARD_CHART_CONFIG[module]?.charts || {};
  }

  static getAvailableModules(): string[] {
    return Object.keys(DASHBOARD_CHART_CONFIG);
  }

  static extractDataFromResponse(response: any, dataPath: string): any {
    if (!response || !dataPath) return null;
    return dataPath
      .split(".")
      .reduce((obj, key) => obj?.[key] ?? null, response);
  }

  static getChartComponent(chartType: string): string {
    return (
      CHART_COMPONENT_MAP[chartType as keyof typeof CHART_COMPONENT_MAP] ||
      "DefaultChartComponent"
    );
  }

  private static findInModule<T extends MetricConfig | ChartConfig>(
    searchId: string,
    section: "metrics" | "charts",
  ): { module: string; key: string; config: T } | null {
    for (const [module, moduleConfig] of Object.entries(
      DASHBOARD_CHART_CONFIG,
    )) {
      const items = moduleConfig[section] as Record<string, T>;
      for (const [key, config] of Object.entries(items)) {
        if (config.id === searchId) {
          return { module, key, config };
        }
      }
    }
    return null;
  }

  static getMetricById(
    metricId: string,
  ): { module: string; key: string; config: MetricConfig } | null {
    return this.findInModule<MetricConfig>(metricId, "metrics");
  }

  static getChartById(
    chartId: string,
  ): { module: string; key: string; config: ChartConfig } | null {
    return this.findInModule<ChartConfig>(chartId, "charts");
  }

  static getConfigKeyFromId(
    id: string,
    module: string,
    type: "metric" | "chart",
  ): string | null {
    const items =
      type === "metric"
        ? this.getModuleMetrics(module)
        : this.getModuleCharts(module);
    return (
      Object.entries(items).find(([, config]) => config.id === id)?.[0] || null
    );
  }

  static getComponentById(id: string): ComponentResult | null {
    const metricResult = this.getMetricById(id);
    if (metricResult) return { ...metricResult, type: "metric" };

    const chartResult = this.getChartById(id);
    if (chartResult) return { ...chartResult, type: "chart" };

    return null;
  }
}
